import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { ProcedureName98 } from "../../assets/data/Constants";
import { GlobalContext } from "../../context/globalContext";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkLabelText from "../../components/atoms/TkLabelText";
import TkButton from "../../components/atoms/TkButton";
import TkResigneeSearchModal from "../../components/molecules/TkResigneeSearchModal";
import { IAPI_AGE009_Request } from "../../type/apiType";
import { RetireeList } from "../../type/apiType/IAPI_RET019";
import { httpAPI_AGE006, httpAPI_AGE009, httpAPI_RET019 } from "./api/request";
import {
  makeAgentList,
  makeFacilityList,
  makeTaskList,
} from "./hooks/useHooks";
import { fakeTableList, schema } from "./parts";
import "./_style.scss";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import _ from "lodash";
import { ClearIcon } from "@mui/x-date-pickers";
import TkDialog from "../../components/atoms/TkDialog";

/**
 * SCRC100005 代行者への新規依頼
 * @returns JSX.Element
 */
const SCRC100005 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [open, setOpen] = useState<boolean>(false);
  const [retireeList, setRetireeList] = useState<RetireeList[]>([]);
  const [staffData, setStaffData] = useState({ name: "", no: "" });
  const [retirementFlg, setRetireFlg] = useState(false);
  const [saveData, setSaveData] = useState<any>();
  const [openSave, setOpenSave] = useState(false);

  // 初期表示
  const handleInit = async () => {
    // 代行入力依頼新規API 呼び出し
    try {
      const res = await httpAPI_AGE006({
        contractorNo: state.authInfo?.contractorNo,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;

        // 代行者
        resArr.find(
          (item) => item.keyword === "agentUserName"
        )!.selectPropsList = makeAgentList(res.agentList);

        // 代行内容
        let formerTaskType = resArr.find(
          (item) => item.keyword === "formerTaskType"
        );
        if (formerTaskType) {
          formerTaskType.selectPropsList = makeTaskList(res.agentContentList);
          formerTaskType.onGetValue = (value: any) => {
            handleChangeFormerTaskType(value);
          };
        }

        // 対象施設
        resArr.find(
          (item) => item.keyword === "agentFacilityName"
        )!.selectPropsList = makeFacilityList(res.facilityList);

        setStatecorpInfoList(resArr);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  function handleChangeFormerTaskType(value: any) {
    const nowRetirementFlg = value.type === ProcedureName98["退職届"];
    setRetireFlg(nowRetirementFlg);
    let resArr = stateFakeTableList.current;
    let retirementStaffName = resArr.find(
      (item) => item.keyword === "retirementStaffName"
    )!;
    let agentFacilityName = resArr.find(
      (item) => item.keyword === "agentFacilityName"
    )!;
    if (nowRetirementFlg) {
      agentFacilityName.setDefaultValue = "";
    } else {
      setStaffData({ no: null as any, name: null as any });
    }
    agentFacilityName.showLine = !nowRetirementFlg;
    retirementStaffName.showLine = nowRetirementFlg;
    resArr.find((item) => item.keyword === "requestTitle")!.setDefaultValue =
      value.taskTitle;
    resArr.find((item) => item.keyword === "dueDate")!.setDefaultValue =
      value.dueDate;
    resArr.find((item) => item.keyword === "requestContent")!.setDefaultValue =
      value.taskText;

    setStatecorpInfoList([...resArr]);
  }

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (data?: any) => {
    // 退職者検索API 呼び出し
    try {
      const res = await httpAPI_RET019({
        contractorNo: state.authInfo?.contractorNo,
        staffNo: data?.staffNo,
        retirementStaffName: data?.retirementStaffName,
        retirementStaffBirth: data?.retirementStaffBirth,
      });

      if (res.resultCd === "I0000") {
        setRetireeList(res.fileList!);
        setOpen(true);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleClear = () => {
    setStaffData({ no: null as any, name: null as any });
  };

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let retirementStaffName = resArr.find(
      (item) => item.keyword === "retirementStaffName"
    )!;

    let staffInfo = staffData.no
      ? staffData.no + ":" + (staffData.name ?? "")
      : "";
    retirementStaffName.node = (
      <Box className="retirementStaffName">
        <TkLabelText
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClear}
                  edge="end"
                  sx={{ padding: 0.5, marginRight: 0.2 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          wantToSetValue={staffInfo}
          disabled
        />
        <TkButton onClick={handleSearch}>退職者検索</TkButton>
      </Box>
    );

    setStatecorpInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffData]);

  const handleSubmit = async (data: any) => {
    if (
      !(
        data.formerTaskType.type ===
          ProcedureName98["配置換え（対象外異動含む）"] ||
        data.formerTaskType.type === ProcedureName98["退職届（施設単位）"]
      ) &&
      data.agentFacilityName === "999"
    ) {
      setState({
        ...state,
        errorMsgId: "TKMSG_SCR0113E",
      });
      return;
    }
    if (retirementFlg && !staffData.no) {
      let resArr = stateFakeTableList.current;
      let retirementStaffName = resArr.find(
        (item) => item.keyword === "retirementStaffName"
      )!;

      retirementStaffName.node = (
        <Box className="retirementStaffName">
          <TkLabelText
            wantToSetValue={""}
            disabled
            error={true}
            helperText={TkPropertyMessageGet("TKMSG_SCR0044E")}
          />
          <TkButton onClick={handleSearch}>退職者検索</TkButton>
        </Box>
      );

      return;
    }

    setSaveData(data);
    setOpenSave(true);
  };

  const requestUpdate = async () => {
    setOpenSave(false);
    const paramData: IAPI_AGE009_Request = {
      taskId: null,
      formerTaskId: saveData.formerTaskType.id,
      userId: saveData.agentUserName,
      facilityNo: saveData.agentFacilityName,
      formerTaskType: saveData.formerTaskType.type,
      taskTitle: saveData.requestTitle,
      taskText: saveData.requestContent,
      dueDate: saveData.dueDate?.replaceAll?.("-", "/"),
      staffNo: staffData.no,
    };

    // 代行入力依頼更新API 呼び出し
    try {
      const res = await httpAPI_AGE009({
        ...paramData,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC100001");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleSave = (e: any) => {
    setStaffData({ no: e.staffNo, name: e.retirementStaffName });
    setOpen(false);
  };

  return (
    <Box className="SCRC100005" data-testid="SCRC100005PageForTest">
      <TkFakeTable
        showButton={false}
        list={statecorpInfoList}
        ruleSchema={
          retirementFlg ? schema : { ...schema, ...schema.stateSchema }
        }
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          {
            submitButtonText: "代行者に新規依頼",
          },
        ]}
        onCheckAndSubmit={handleSubmit}
      />

      {/* 退職者検索ダイアログ表示 */}
      <TkResigneeSearchModal
        open={open}
        listData={retireeList}
        isHoujin={true}
        onChangeSearchData={handleSearch}
        onClose={() => setOpen(false)}
        onSave={handleSave}
      />

      {/* 確認ダイアログ表示 */}
      <TkDialog
        title="確認（代行者に依頼します）"
        contextText="　代行者へ依頼をします。問題ないでしょうか？"
        open={openSave}
        okText="はい"
        cancelText="いいえ"
        onCancel={() => setOpenSave(false)}
        onSave={() => requestUpdate()}
      />
    </Box>
  );
};

export default SCRC100005;
